import { useModals } from '@/components/ModalManager/useModals'
import { Button } from '@/components/_ui/Button'
import T from '@/components/Helpers/Translate'
import { toAlpha3 } from '@/lib/utils/localization/toAlpha3'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import useGlobalStore from '@/store/useGlobalStore'
import type { SupportedAlpha2CountryCode } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import type { MarketExceptionAvailableNfrMarkets } from '@/constants/MarketExceptions/MarketExceptionTypes'
import { countryData } from '@/constants/pickerRegions/countryData'
import type { SupportedLanguage } from '@/lib/utils/localization/getSupportedLanguages'

export const useNfrNavigationModal = () => {
  const { modals } = useModals()
  const { clientRedirect } = useClientRedirect()
  const {
    app: { language },
  } = useGlobalStore()

  const handleNfrRedirect = async (market: SupportedAlpha2CountryCode) => {
    const country = countryData.find((c) => c.alpha2 === market)
    let redirectLanguage: SupportedLanguage = language as SupportedLanguage
    if (country && country.languages && country.languages.length > 0) {
      const isValidLanguage = country.languages.some(
        (lang) => lang.code === language,
      )
      if (!isValidLanguage) {
        const defaultLang = country.languages.find((lang) => lang.default)
        if (defaultLang) {
          redirectLanguage = defaultLang.code as SupportedLanguage
        } else {
          redirectLanguage = country.languages[0].code as SupportedLanguage
        }
      }
    }
    const redirectPath = `/${toAlpha3(market)}/${redirectLanguage}/products`
    modals.closeModals()
    await clientRedirect.to({ path: redirectPath, withPersistingLocale: false })
  }

  const displayNfrNavigationModal = (
    nfrMarket: MarketExceptionAvailableNfrMarkets,
  ) => {
    modals.openModal({
      type: 'dialogOrDrawer',
      modalId: 'nfr-navigation-modal',
      modal: {
        children: (
          <div className="p-5">
            <h2 className="mb-4 text-lg font-semibold">
              {nfrMarket.market} products - NFR
            </h2>
            <p className="mb-4">
              <span className="font-bold text-red-500">Please note:</span>{' '}
              <T>{nfrMarket.modalDisclaimer}</T>
            </p>
            <div className="mt-6 flex justify-end">
              <Button
                className="h-[50px] w-[100px] bg-secondary p-6 text-lg text-white"
                size="lg"
                data-test="nfr-modal-ok"
                onClick={() => handleNfrRedirect(nfrMarket.market)}
              >
                Ok
              </Button>
            </div>
          </div>
        ),
      },
    })
  }

  return { displayNfrNavigationModal }
}
